import { useCallback, useMemo, useState } from 'react';
import { AppointmentPerson } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { isEmpty, sortBy } from 'lodash-es';
import { useInfiniteQuery } from 'react-query';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonAPI } from '@frontend/api-person';
import { ScheduleQueries } from '@frontend/api-schedule';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { useAlert, useDebouncedValue, useForm } from '@frontend/design-system';
import { Option } from '../../../../../components/multi-view-combobox';
import { DATETIME_FORMATS } from '../../../../../constants';
import { getTodaysDate } from '../../../../../utils/date-helpers';
import { PersonSelectorTypes } from '../../../components/PersonSelector';
import { transformToLegacyPerson } from '../../../helpers';

const PERSON_LIST_API_RESPONSE_LIMIT = 20;

type SelectedOption = Option | undefined;

const initialSelectedOption = (
  configuredCalendarEvent: ReturnType<typeof useCalendarEventsConfigurationShallowStore>['configuredCalendarEvent']
) => {
  if (configuredCalendarEvent?.patientId) {
    return {
      id: configuredCalendarEvent?.patientId || '',
      label: configuredCalendarEvent?.name || '',
    };
  }
  return undefined;
};

export const usePatientSelector = ({
  isIntegratedOffice,
  isScheduleV3,
  locationId = '',
  setPersonValue,
}: {
  isIntegratedOffice: boolean;
  isScheduleV3: boolean;
  locationId: string;
  setPersonValue: (person: AppointmentPerson) => void;
}) => {
  const alerts = useAlert();
  const { t } = useTranslation('scheduleCalendarEvents');
  const { configuredCalendarEvent } = useCalendarEventsConfigurationShallowStore('configuredCalendarEvent');
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');

  const [selectedOption, setSelectedOption] = useState<SelectedOption>(() =>
    initialSelectedOption(configuredCalendarEvent)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // PatientForm
  const patientForm = useForm({
    fields: {
      firstName: {
        type: 'text',
        value: '',
        required: true,
      },
      lastName: {
        type: 'text',
        value: '',
        required: true,
      },
      dob: {
        type: 'datePicker',
        value: '',
        required: true,
        maxDate: getTodaysDate(),
      },
      phone: {
        type: 'phone',
        value: '',
        required: true,
      },
    },
  });

  // Add contact mutation
  const addCustomContactMutation = ScheduleQueries.useMutateAddCustomContact(locationId);

  // Fetch patients query
  const infinitePersonQuery = useInfiniteQuery({
    queryKey: ['personsV3', debouncedSearchTerm, locationId],
    queryFn: async ({ pageParam }) => {
      const res = await PersonAPI.getPersonsV3({
        pageToken: pageParam,
        responseLimit: PERSON_LIST_API_RESPONSE_LIMIT,
        search: debouncedSearchTerm,
        sourceIds: demoSourceIds,
        statusFilter: '',
        locationId,
      });
      return {
        nextOffset: res.nextOffset,
        rows: sortBy(
          res.rows.map((person) => transformToLegacyPerson(person)),
          [(person) => (person.LastName ? person.LastName?.toLowerCase() : person.FirstName?.toLowerCase())]
        ),
      };
    },
    getNextPageParam: ({ nextOffset, rows }) => {
      if (rows.length < PERSON_LIST_API_RESPONSE_LIMIT) return undefined;
      return nextOffset;
    },
    enabled: !!locationId,
  });

  const contactOptions = useMemo(() => {
    if (!infinitePersonQuery.data?.pages) return [];
    return infinitePersonQuery.data.pages.flatMap((page) =>
      page.rows.map((person) => ({
        id: person.PersonID,
        label: getFullName(person),
      }))
    );
  }, [infinitePersonQuery.data?.pages]);

  const onSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onPatientChange = useCallback(
    (value: string | string[]) => {
      setSelectedOption(contactOptions.find((option) => option.id === value));
    },
    [contactOptions]
  );

  const onPatientRemove = useCallback(
    (value: string) => {
      if (selectedOption?.id === value) {
        setSelectedOption(undefined);
      }
    },
    [selectedOption]
  );

  // Handle add new contact
  const handleAddCustomerContact = useCallback(
    async (formValues: PersonSelectorTypes.PersonSelectorFormValues) => {
      try {
        const customContactData = await addCustomContactMutation.mutateAsync({
          first_name: formValues?.firstName ?? '',
          last_name: formValues?.lastName ?? '',
          phone_mobile: formValues?.phoneNumber ?? '',
          birthdate: formValues?.dateOfBirth ?? '',
        });
        const personId = customContactData?.data?.person_id;

        if (!personId) return;

        // Fetch Person details and add to the patient selection, if person_id is returned from the create person API
        try {
          const person = await PersonAPI.getPersonExtended(personId);
          setPersonValue({
            firstName: person.FirstName,
            lastName: person.LastName,
            personId: person.PersonID,
            email: person.Email,
            mobilePhone: person.MobilePhone,
            homePhone: person.HomePhone,
            sourceId: person.SourceID,
            preferredName: person.PreferredName ?? '',
            pmId: person.PMID ?? '',
            pmDisplayId: person.PMDisplayID ?? '',
            gender: person.Gender,
            status: person.Status,
            birthDate: person.Birthdate,
          });
          setSelectedOption({
            id: person.PersonID,
            label: getFullName(person),
          });

          alerts.success(t('Contact created successfully'));
          return personId;
        } catch (error) {
          console.error(t('Error selecting created patient'), error);
          return;
        }
      } catch (err) {
        console.error(t('Error creating contact'), err);
        return;
      }
    },
    [addCustomContactMutation, alerts, setPersonValue, t]
  );

  const onPrimaryAction = useCallback(async () => {
    if (isIntegratedOffice && !isScheduleV3) {
      return;
    }

    const personId = await handleAddCustomerContact({
      firstName: patientForm.values.firstName ?? '',
      lastName: patientForm.values.lastName ?? '',
      phoneNumber: patientForm.values.phone ?? '',
      dateOfBirth: formatDate(patientForm.values.dob, DATETIME_FORMATS.ISO_DATE),
    });

    if (personId) {
      patientForm.reset();
    }

    return !!personId;
  }, [handleAddCustomerContact, isIntegratedOffice, isScheduleV3, patientForm]);

  return {
    infinitePersonQuery,
    isCreatingContact: addCustomContactMutation.isLoading,
    isCreateContactDisabled: !patientForm.isComplete || !isEmpty(patientForm.getErrors()),
    patientForm,
    selectedPatientOption: selectedOption,
    setSelectedPatientOption: setSelectedOption,
    onPrimaryAction,
    onPatientChange,
    onPatientRemove,
    onSearchChange,
  };
};
